import {
  ParsedGoldItem,
  GoldPrice,
  GoldPriceResponse,
  GoldPriceData,
  GramRow,
  CityRow,
  CountryRow,
  CityItem,
  ParsedCityPrice,
} from "@/type/goldPageType";
import { formatDate, formatIndianNumber } from "./commonFunctions";
import { NODE_API_URL, ROOT_URL } from "./gloableVariables";

export function formatInr(value: number): string {
  return `₹${value.toLocaleString("en-IN")}`;
}

export function formatForeignPrice(value: number, symbol: string): string {
  return `${symbol} ${value}`;
}

export function transformGoldData(data: GoldPrice[] | null | undefined): ParsedGoldItem[] {
  if (!data) return [];

  return data.map((item) => {
    const goldType = Number(item.type.replace(/[^0-9]/g, ""));
    const todaysPrice = Number(item.price.replace(/[^0-9]/g, ""));
    const change = Number(item.change.replace(/[^0-9-]/g, ""));
    const yesterdaysPrice = todaysPrice - change;

    return {
      goldType,
      todaysPrice,
      yesterdaysPrice,
      change,
    };
  });
}

export async function fetchGoldData(): Promise<GoldPriceResponse | null> {
  try {
    const response = await fetch(`${NODE_API_URL}gold/gold-rates`, {
      method: "GET",
      next: {
        revalidate: 60,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error("API response does not contain required data.");
    }

    return data as GoldPriceResponse; // Return the data casted as GoldPriceResponse
  } catch (error) {
    console.error("Error fetching gold data:", error instanceof Error ? error.message : error);
    return null;
  }
}

export function extractCleanGoldData(data: GoldPriceData) {
  // Helper to extract number only
  const toNum = (value: string): number => Number(value.replace(/[^0-9.]/g, ""));

  // ---- Extract clean cities ----
  const cities = data.cities.map((item) => ({
    city: item.city,
    gold_24k: toNum(item.gold_24k),
    gold_22k: toNum(item.gold_22k),
    gold_18k: toNum(item.gold_18k),
  }));

  // ---- Extract clean countries ----
  const countries = data.countries.map((item) => ({
    category: item.category,
    rates: item.rates.map((rate) => ({
      country: rate.country,
      symbol: rate.symbol,
      price: toNum(rate.price),
      price_in_inr: toNum(rate.price_in_inr),
    })),
  }));

  // ---- Only list of city names ----
  const cityNames = data.cities.map((c) => c.city);

  // ---- Only list of country names ----
  const countryNames = data.countries.flatMap((cat) => cat.rates.map((r) => r.country));

  return {
    cities,
    countries,
    cityNames,
    countryNames,
  };
}

// ----------------- Getting City Gold Data by City Name ----------
export function getCityGoldPrice(data: GoldPriceData, cityName: string) {
  // convert string → number safely
  const toNum = (value: string): number => Number(value.replace(/[^0-9.]/g, ""));

  // Find city (case-insensitive match)
  const city = data.cities.find((c) => c.city.toLowerCase() === cityName.toLowerCase());

  if (!city) return null;

  return {
    city: city.city,
    gold_24k: toNum(city.gold_24k),
    gold_22k: toNum(city.gold_22k),
    gold_18k: toNum(city.gold_18k),
  };
}

// ------------ Check URL For Invalid Data
export function validateCitySlug(slug: string, cityList: string[]) {
  const exists = cityList.some((city) => city.toLowerCase() === slug.toLowerCase());

  return exists;
}

// ------------- All Tables Format Function
export function buildIndiaGoldRateTable(item: {
  goldType: number;
  todaysPrice: number;
  yesterdaysPrice: number;
  change: number;
}): GramRow[] {
  const GRAM_VALUES = [1, 8, 10, 100];
  return GRAM_VALUES.map((g) => {
    const today = item.todaysPrice * g;
    const yesterday = item.yesterdaysPrice * g;
    const change = today - yesterday;

    return {
      gram: `${g}`,
      today: formatInr(today),
      yesterday: formatInr(yesterday),
      change: `${change >= 0 ? "+ " : "- "}${formatInr(Math.abs(change))}`,
    };
  });
}

export function buildCitiesTable(
  rows: {
    city: string;
    gold_24k: number;
    gold_22k: number;
    gold_18k: number;
  }[],
): CityRow[] {
  return rows.map((item) => ({
    city: item.city,
    "24K-today": formatInr(item.gold_24k),
    "22K-today": formatInr(item.gold_22k),
    "18K-today": formatInr(item.gold_18k),
  }));
}

export function buildCountryTable(
  rates: {
    country: string;
    symbol: string;
    price: number;
    price_in_inr: number;
  }[],
): CountryRow[] {
  return rates.map((r) => ({
    country: r.country,
    price: formatForeignPrice(r.price, r.symbol),
    "price-inr": formatInr(r.price_in_inr),
  }));
}

export function buildAllTables(data: {
  india: {
    goldType: number;
    todaysPrice: number;
    yesterdaysPrice: number;
    change: number;
  }[];
  cities: {
    city: string;
    gold_24k: number;
    gold_22k: number;
    gold_18k: number;
  }[];
  countries: {
    category: string;
    rates: {
      country: string;
      symbol: string;
      price: number;
      price_in_inr: number;
    }[];
  }[];
}) {
  return {
    india24: buildIndiaGoldRateTable(
      data.india.find((d) => d.goldType === 24) ?? {
        goldType: 24,
        todaysPrice: 0,
        yesterdaysPrice: 0,
        change: 0,
      },
    ),
    india22: buildIndiaGoldRateTable(
      data.india.find((d) => d.goldType === 22) ?? {
        goldType: 22,
        todaysPrice: 0,
        yesterdaysPrice: 0,
        change: 0,
      },
    ),
    india18: buildIndiaGoldRateTable(
      data.india.find((d) => d.goldType === 18) ?? {
        goldType: 18,
        todaysPrice: 0,
        yesterdaysPrice: 0,
        change: 0,
      },
    ),

    cities: buildCitiesTable(data.cities),

    countries24: buildCountryTable(data.countries.find((c) => c.category === "24k")?.rates ?? []),
    countries22: buildCountryTable(data.countries.find((c) => c.category === "22k")?.rates ?? []),
    countries18: buildCountryTable(data.countries.find((c) => c.category === "18k")?.rates ?? []),
  };
}

// -------------- All cities with the amount depends on K
export function getCityGoldPrices(cities: CityItem[], purity: 24 | 22 | 18): ParsedCityPrice[] {
  const keyMap: Record<24 | 22 | 18, keyof CityItem> = {
    24: "gold_24k",
    22: "gold_22k",
    18: "gold_18k",
  };

  const selectedKey = keyMap[purity];

  return cities
    .slice()
    .sort((a, b) => a.city.localeCompare(b.city))
    .map((item) => ({
      city: item.city,
      price: Number(item[selectedKey]) * 10, // 👈 Force to number
    }));
}

// ------------------- Returning Static SEO Content
export function goldSEOContent({
  todaysPrice,
  lastUpdated,
}: {
  todaysPrice: ParsedGoldItem[];
  lastUpdated: Date | string;
}) {
  const gold24K = todaysPrice[0].todaysPrice;
  const gold22K = todaysPrice[1].todaysPrice;
  const gold18K = todaysPrice[2].todaysPrice;

  const change24K = todaysPrice[0].change;
  const change22K = todaysPrice[1].change;
  const change18K = todaysPrice[2].change;

  const content = `
  <h2><strong>What is the Gold Rate Today in India?</strong></h2>
<p>The gold price in India, as of ${formatDate(lastUpdated)}, is Rs ${formatIndianNumber(gold24K)} per gram for 24-carat gold, while the rate for 22-carat gold is Rs ${formatIndianNumber(gold22K)} per gram. Gold prices have slightly ${change24K > 0 ? "increased" : "decreased"} today by Rs ${formatIndianNumber(change24K)} for 24K and Rs ${formatIndianNumber(change22K)} for 22K per gram. The rate of 18-carat gold is Rs ${formatIndianNumber(gold18K)} per gram, which also slightly ${
    change18K > 0 ? "increase" : "decrease"
  } by Rs ${formatIndianNumber(change18K)}. Gold prices in India can vary slightly across different cities due to local taxes and logistics costs.</p>
<h2>
  <strong><strong>Factors Affecting Gold Prices in India</strong></strong>
</h2>
<p>The following are the primary factors that affect gold rates in India:</p>
<h3>
  <strong><strong>Global Factors</strong></strong>
</h3>
<ul>
  <li>
    <strong><strong>Gold Prices- </strong></strong>The gold price is set in international markets in US Dollars. Hence, changes in these global rates directly impact the prices in India.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>US Dollar Value- </strong></strong>The strength of the Indian Rupee (INR) against the US Dollar (USD) is crucial, as a weaker rupee increases the cost of imported gold in currency.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>Inflation and Interest Rates- </strong></strong>Gold is often used as a hedge against inflation. When interest rates rise, other options become more appealing, resulting in lower gold demands.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>Global Uncertainty- </strong></strong>During times of global and economic instability, many individuals turn to gold as a safe asset, increasing both demand for gold and its prices.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>Central Bank Reserves-</strong></strong
    >Central banks, including the RBI, buy and sell gold as part of their foreign exchange reserves, which can affect the market sentiment and prices.
  </li>
</ul>
<h3>
  <strong><strong>Domestic Factors</strong></strong>
</h3>
<ul>
  <li>
    <strong><strong>Demand and Supply- </strong></strong>India is one of the largest consumers of gold globally and its demand can surge during festivals, in the wedding season, etc, impacting local prices.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>Government Policies and Import Duties- </strong></strong>India imports most of its gold, so government policies like import duties and taxes directly affect the local gold rates.
  </li>
</ul>
<ul>
  <li>
    <strong><strong>Local Market Factors- </strong></strong>Prices can change slightly in different Indian cities. This happens because of transportation costs, local taxes and the impact of local jewelry groups.
  </li>
</ul>
<h2>
  <strong><strong>Best Gold Investment Options in India</strong></strong>
</h2>
<p>Here are the best options in India to invest in gold and earn high growth:</p>
<ul>
  <li>
    <h3>
      <strong
        ><strong>Gold Exchange-Traded Funds (ETFs)<br /></strong
      ></strong>
    </h3>
    These are the units that represent physical gold in demat form that are traded on the stock exchanges like shares. It offers high liquidity, purity, secure storage and transparency.
  </li>
</ul>
<ul>
  <li>
    <h3>
      <strong
        ><strong>Gold Mutual Funds (Fund of Funds)<br /></strong
      ></strong>
    </h3>
    These gold mutual funds primarily invest in the units of the Gold ETFs. It is a convenient way to invest without a demat account. It also allows diversification and small investments through SIP.
  </li>
</ul>
<ul>
  <li>
    <h3>
      <strong
        ><strong>Sovereign Gold Bonds (SGBs) <br /></strong
      ></strong>
    </h3>
    Investment in SGB is the best gold investment plan in India. These are the government-backed securities issued by the RBI, making them the safest option with a sovereign guarantee. It also offers interest and tax benefits with purity.
  </li>
</ul>
<ul>
  <li>
    <h3>
      <strong
        ><strong>Physical Gold<br /></strong
      ></strong>
    </h3>
    It is the traditional and oldest method of purchasing gold in tangible or physical form, like jewellery, coins, bars, etc, for cultural significance. It can also be used as collateral for gold loans.
  </li>
</ul>
<h2>
  <strong><strong>Is Gold a Good Investment in India for Growth?</strong></strong>
</h2>
<p>Gold is one of the best investment options in India for long-term wealth preservation and as a strategic asset for portfolio diversification. The following are the benefits and reasons why gold investment is good in India:</p>
<ul>
  <li>Gold provides a hedge against inflation and offers protection for your savings during inflation in India.</li>
  <li>During domestic or global crises, gold is the safest option and an essential part of a strong investment portfolio.</li>
  <li>Adding gold to your investments can lower the overall risk and make your portfolio less volatile.</li>
  <li>When the rupee weakens, gold provides a natural way to protect against the drop in value of our currency.</li>
  <li>Gold is a highly liquid asset and you can convert your gold into cash easily and quickly.</li>
  <li>Gold investment options like SGBs provide tax benefits, making them a great option for long-term investment and growth.</li>
</ul>
<h2>
  <strong><strong>Taxes on Gold in India</strong></strong>
</h2>
<p>Here are the tax rates that are applied while purchasing and selling gold in India:</p>
<h3>
  <strong><strong>Taxes on Buying Gold</strong></strong>
</h3>
<ul>
  <li>A 3% GST is imposed on the gold value for all forms across India.</li>
  <li>An additional 5% GST is charged on the making cost of the gold jewellery.</li>
  <li>There is a customs duty of 6% for buying imported gold, with a Basic Customs Duty of 5% and an additional 1% for Agriculture Infrastructure and Development Cess.</li>
</ul>
<h3>
  <strong><strong>Taxes on Selling Gold </strong></strong>
</h3>
<p>When you sell gold, the gain is classified as either short-term gain or long-term capital gain, depending on the holding period. Here are the tax rates for both capital gains:</p>
<table style="width: 100%; border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; max-width: 900px; margin: 0 auto">
  <thead>
    <tr>
      <th style="text-align: left; padding: 10px 12px; border: 1px solid #ddd; background: #f6f6f6; font-weight: bold">Form of Gold</th>
      <th style="text-align: left; padding: 10px 12px; border: 1px solid #ddd; background: #f6f6f6; font-weight: bold">Short-Term Capital Gains (STCG)</th>
      <th style="text-align: left; padding: 10px 12px; border: 1px solid #ddd; background: #f6f6f6; font-weight: bold">Long-Term Capital Gains (LTCG)</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Physical Gold / Digital Gold</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Held for a shorter period; gains are taxed at your applicable income tax slab rate.</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Held for a longer period; taxed at a flat rate without indexation benefit.</td>
    </tr>
    <tr>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Gold ETFs</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Held for a shorter period; gains are taxed at your applicable income tax slab rate.</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Held for a longer period; taxed at a flat rate without indexation benefit.</td>
    </tr>
    <tr>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Sovereign Gold Bonds (SGBs)</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Held for a shorter period (if sold on exchange); taxed at your applicable income tax slab rate.</td>
      <td style="padding: 10px 12px; border: 1px solid #ddd; vertical-align: top">Fully exempt from tax if held until maturity. If sold in the secondary market before maturity, LTCG (after a certain holding period) is taxed at a flat rate without indexation benefit.</td>
    </tr>
  </tbody>
</table>
<p>As of recent changes in July 2024, indexation benefits have been removed for most gold investments in India.</p>
`;

  return content;
}

// ------------------- Builing Static meta
export async function buildCityGoldMeta(pageTitle: string, pageDescription: string, canonicalUrl?: string) {
  const thumb = "https://www.mysiponline.com/images/whatsapp.jpg";

  return {
    title: pageTitle ?? undefined,
    description: pageDescription ?? undefined,
    keywords: "",
    authors: [{ name: "https://www.mysiponline.com/" }],
    robots: process.env.APP_ENV === "production" ? "index, follow" : "noindex nofollow",
    manifest: "/favicons/manifest.json",
    icons: {
      icon: [
        {
          url: `${ROOT_URL}favicons/android-chrome-192x192.png`,
          sizes: "192x192",
          type: "image/png",
        },
      ],
    },
    appleWebApp: {
      capable: true,
      statusBarStyle: "default",
    },
    openGraph: {
      title: pageTitle ?? undefined,
      description: pageDescription ?? undefined,
      url: canonicalUrl,
      type: "article",
      siteName: "mysiponline.com",
      images: [
        {
          url: thumb,
          alt: pageTitle ?? undefined,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      site: "@mysiponline",
      creator: "@MySIPonline",
      title: pageTitle ?? undefined,
      description: pageDescription ?? undefined,
      images: [thumb],
    },
    other: {
      "msvalidate.01": "7F762C06323514E4EA99795EF2C3DDEB",
      SKYPE_TOOLBAR: "SKYPE_TOOLBAR_PARSER_COMPATIBLE",
      "facebook-domain-verification": "309y7rvky92av0xs4tdrymmo6xct3z",
      copyright: `Copyright ${new Date().getFullYear()} @ mysiponline.com`,
    },
    canonical: canonicalUrl,
  };
}
